/**
 * Os cinco estados de tela — G7 §20.
 *
 *   loading · empty · success · error · unauthorized
 *
 * POR QUE ISTO É UM MÓDULO, E NÃO UM PADRÃO
 *
 *   O §110 tem uma frase que é a razão inteira deste arquivo:
 *
 *     _"Falha de consulta não pode parecer lista vazia."_
 *
 *   Uma tabela que renderiza `rows.map(...)` mostra o mesmo nada para "não há
 *   documentos" e para "a consulta falhou". No primeiro caso a informação é
 *   verdadeira; no segundo, o console está afirmando ao contribuinte que ele não
 *   tem documentos — sobre uma pergunta que nunca foi respondida.
 *
 *   Num painel fiscal isso não é um detalhe de UX. "Você não tem nota nenhuma" é
 *   uma afirmação sobre a situação fiscal de uma empresa.
 *
 *   Deixar cada tela lidar com isso por conta própria é garantir que uma delas
 *   esqueça. `<AsyncPanel>` recebe o resultado do TanStack Query inteiro e não
 *   deixa escolher errado: quem passa `isError` ganha tela de erro.
 */

import type { ReactNode } from 'react';

export function LoadingState({ label = 'Carregando…' }: { label?: string }) {
  return (
    <div className="of-panel-state of-panel-state--loading" role="status" aria-live="polite">
      <span className="of-panel-state__spinner" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({ title, detail }: { title: string; detail?: string }) {
  return (
    <div className="of-panel-state of-panel-state--empty">
      <strong className="of-panel-state__title">{title}</strong>
      {detail !== undefined && <p className="of-panel-state__detail">{detail}</p>}
    </div>
  );
}

/**
 * O erro mostra a mensagem do provedor, sem tradução.
 *
 * Reescrever "permission denied for table nfse_documents" como "algo deu
 * errado" transforma um diagnóstico preciso num encolher de ombros. Quem opera
 * infraestrutura fiscal precisa da frase original.
 */
export function ErrorState({ title, error }: { title: string; error: unknown }) {
  const mensagem = error instanceof Error ? error.message : String(error);
  return (
    <div className="of-panel-state of-panel-state--error" role="alert">
      <strong className="of-panel-state__title">{title}</strong>
      <p className="of-panel-state__detail">A consulta falhou. Isto não significa ausência de dados.</p>
      <code className="of-panel-state__code">{mensagem}</code>
    </div>
  );
}

/**
 * O texto do §62, literal.
 *
 * Ele distingue as duas perguntas do §53 numa frase só: a autenticação
 * funcionou, e a autorização fiscal é outra coisa. Trocar por "acesso negado"
 * apagaria a metade que informa.
 */
export function UnauthorizedState() {
  return (
    <div className="of-panel-state of-panel-state--unauthorized" role="alert">
      <strong className="of-panel-state__title">
        Usuário autenticado, mas sem acesso a uma identidade fiscal.
      </strong>
      <p className="of-panel-state__detail">
        A conta existe no Supabase Auth. O que falta é o vínculo canônico entre ela e uma
        identidade fiscal — <code className="of-mono">account_fiscal_identity_access</code>.
        Existir em <code className="of-mono">auth.users</code> não concede acesso fiscal.
      </p>
    </div>
  );
}

export interface AsyncPanelProps<T> {
  readonly query: {
    readonly isPending: boolean;
    readonly isError: boolean;
    readonly error: unknown;
    readonly data: T[] | undefined;
  };
  readonly title: string;
  readonly emptyTitle: string;
  readonly emptyDetail?: string;
  children(rows: T[]): ReactNode;
}

/**
 * A ordem das perguntas é a proteção.
 *
 * `isPending` -> `isError` -> vazio -> conteúdo. Perguntar "está vazio?" antes
 * de "falhou?" é como uma falha vira uma lista vazia, e é justamente a inversão
 * que o §110 proíbe.
 */
export function AsyncPanel<T>({
  query,
  title,
  emptyTitle,
  emptyDetail,
  children,
}: AsyncPanelProps<T>) {
  if (query.isPending) return <LoadingState />;
  if (query.isError) return <ErrorState title={title} error={query.error} />;
  const rows = query.data ?? [];
  if (rows.length === 0) return <EmptyState title={emptyTitle} {...(emptyDetail !== undefined ? { detail: emptyDetail } : {})} />;
  return <>{children(rows)}</>;
}

/** Rótulo de dado ausente, num lugar só. */
export function ausente(v: string | number | null | undefined): string {
  return v === null || v === undefined || v === '' ? '—' : String(v);
}

/** Data em pt-BR; `null` continua sendo ausência, não "01/01/1970". */
export function dataBr(v: string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
}
